import {sendCustomMetric} from '@github-ui/stats'
import {useEffect, useRef, useState} from 'react'

export type AssigneePickerMetricScope = 'issue' | 'repository'
export type AssigneePickerMetricsSurface =
  | 'issue_page'
  | 'issues_list_filter'
  | 'issue_create'
  | 'issues_bulk'
  | 'pr_overview'
  | 'pr_list_filter'
  | 'pr_list_bulk'
  | 'pr_composer'
  | 'planning_bulk'
  | 'unspecified'
type FetchKind = 'initial' | 'search'
type FetchCache = 'hit' | 'miss' | 'unknown'
export type AssigneePickerMetricPicker = AssigneePickerMetricScope | 'data_source'

export function getFetchMetricScope(number: number | undefined): AssigneePickerMetricScope {
  return number !== undefined ? 'issue' : 'repository'
}

export function getFetchMetricKind(query: string | undefined) {
  return query ? 'search' : 'initial'
}

export function reportAssigneeFetch({
  scope,
  kind,
  cache,
  surface = 'unspecified',
  durationMs,
  resultCount,
}: {
  scope: AssigneePickerMetricScope
  kind: FetchKind
  cache: FetchCache
  surface?: AssigneePickerMetricsSurface
  durationMs: number
  resultCount: number
}) {
  const tags = {scope, kind, cache, surface}
  sendCustomMetric({name: 'ITEM_PICKER_ASSIGNEES_FETCH_DURATION', value: durationMs, tags})
  sendCustomMetric({name: 'ITEM_PICKER_ASSIGNEES_RESULT_COUNT', value: resultCount, tags})
}

export function reportAssigneeFetchError({
  scope,
  kind,
  surface = 'unspecified',
}: {
  scope: AssigneePickerMetricScope
  kind: FetchKind
  surface?: AssigneePickerMetricsSurface
}) {
  sendCustomMetric({name: 'ITEM_PICKER_ASSIGNEES_FETCH_ERROR', value: 1, tags: {scope, kind, surface}})
}

/**
 * Times a fetch that does not go through the shared query cache (a custom data source or fetcher),
 * so the cache outcome is reported as unknown.
 */
export async function measureUncachedAssigneeFetch<T extends {assignees: unknown[]}>(
  fetch: () => Promise<T>,
  {query, surface}: {query: string; surface?: AssigneePickerMetricsSurface},
): Promise<T> {
  const scope = 'repository'
  const kind = getFetchMetricKind(query)
  const startedAt = performance.now()
  try {
    const response = await fetch()
    reportAssigneeFetch({
      scope,
      kind,
      cache: 'unknown',
      surface,
      durationMs: performance.now() - startedAt,
      resultCount: response.assignees.length,
    })
    return response
  } catch (error) {
    reportAssigneeFetchError({scope, kind, surface})
    throw error
  }
}

/**
 * Reports the time from the picker's first render (it mounts when opened) until its first list is ready.
 * The clock starts during render rather than in an effect so the first render and commit are included, which
 * matters for preloaded opens that are ready immediately. It does not include time before the picker mounts.
 * Reports once per mount.
 */
export function useReportOpenToRender(
  picker: AssigneePickerMetricPicker,
  isReady: boolean,
  surface: AssigneePickerMetricsSurface = 'unspecified',
  preloaded = false,
) {
  const [openedAt] = useState(() => performance.now())
  const reported = useRef(false)

  useEffect(() => {
    if (!isReady || reported.current) return
    reported.current = true
    sendCustomMetric({
      name: 'ITEM_PICKER_ASSIGNEES_OPEN_TO_RENDER_DURATION',
      value: performance.now() - openedAt,
      tags: {picker, surface, preloaded: preloaded ? 'true' : 'false'},
    })
  }, [isReady, openedAt, picker, surface, preloaded])
}
